import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { buildApiUrl } from "../utils/apiConfig";
import {
    formatUsDate,
    isoDateFromInstant,
    parseCompleteByInput,
    toDueAtPayload,
    todayIsoDateInUsEastern,
} from "../utils/completeByDate";
import "../styles/add-task-modal.css";

type Props = {
    open: boolean;
    onClose: () => void;
    taskId: number;
    mode?: 'view' | 'edit';
    onTaskUpdated?: () => void;
    athleteEmail?: string;
    isAthleteEditing?: boolean;
};

type Athlete = {
    email: string;
    firstName: string;
    lastName: string;
    imageUrl?: string;
};

type TaskData = {
    name: string;
    description: string;
    existingFilePath?: string | null;
    dueAt?: string;
    allDay?: boolean;
    file?: File | null;
    sendEmail?: boolean;
    completed?: boolean;
};

export default function ViewTaskModal({ open, onClose, taskId, mode = 'view', onTaskUpdated, athleteEmail, isAthleteEditing = false }: Props) {
    const [taskData, setTaskData] = useState<TaskData>({
        name: "",
        description: "",
        existingFilePath: null,
        dueAt: "",
        allDay: false,
        file: null,
        sendEmail: false,
        completed: false
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string>("");
    const [taskOwnerUsername, setTaskOwnerUsername] = useState<string>("");

    // Athlete selection state (like AddTaskModal)
    const [selectedAthletes, setSelectedAthletes] = useState<Athlete[]>([]);
    const [allAthletesSelected, setAllAthletesSelected] = useState(false);
    const [athleteInput, setAthleteInput] = useState("");
    const [athleteSuggestions, setSuggestions] = useState<Athlete[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [searchingAthletes, setSearchingAthletes] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const isEditMode = mode === 'edit';

    // Fetch task details when modal opens
    useEffect(() => {
        const fetchTaskDetails = async () => {
            if (!open || !taskId) return;

            setLoading(true);
            setError("");

            try {
                const response = await fetch(buildApiUrl(`/api/tasks/${taskId}`), {
                    headers: { Accept: "application/json" },
                    credentials: "include" // Use HTTP-only cookie for auth
                });

                if (response.status === 401) {
                    window.location.href = '/login';
                    return;
                }

                if (response.ok) {
                    const task = await response.json();
                    console.log('Fetched task data:', task); // Debug log
                    setTaskData({
                        name: task.title || "",
                        description: task.description || "",
                        existingFilePath: task.filePath,
                        dueAt: task.dueAt,
                        allDay: task.allDay,
                        file: null,
                        sendEmail: false,
                        completed: task.completed || false
                    });
                    setTaskOwnerUsername(task.athleteUsername || "");

                    // Set athlete selection based on task data
                    console.log('task.allAthletes:', task.allAthletes); // Debug log
                    console.log('task.athletes:', task.athletes); // Debug log

                    if (task.allAthletes) {
                        console.log('Setting allAthletesSelected to true'); // Debug log
                        setAllAthletesSelected(true);
                        setSelectedAthletes([]);
                    } else if (task.athletes && Array.isArray(task.athletes)) {
                        console.log('Fetching athlete details for:', task.athletes); // Debug log
                        // Parse athlete data from new API format
                        const athletePromises = task.athletes.map(async (athleteData: any) => {
                            const email = athleteData.username || athleteData.email;
                            const displayName = athleteData.displayName || "";

                            // Split displayName into first and last name
                            const nameParts = displayName.trim().split(/\s+/);
                            const firstName = nameParts[0] || "";
                            const lastName = nameParts.slice(1).join(" ") || "";

                            try {
                                // Try to fetch full athlete details including image
                                const athleteResponse = await fetch(
                                    buildApiUrl(`api/athletes/${encodeURIComponent(email)}`),
                                    {
                                        headers: { Accept: "application/json" },
                                        credentials: "include" // Use HTTP-only cookie for auth
                                    }
                                );

                                if (athleteResponse.status === 401) {
                                    window.location.href = '/login';
                                    return null;
                                }

                                if (athleteResponse.ok) {
                                    const athlete = await athleteResponse.json();
                                    return {
                                        email: athlete.email || email,
                                        firstName: athlete.firstName || firstName,
                                        lastName: athlete.lastName || lastName,
                                        imageUrl: athlete.imageUrl
                                    };
                                }
                            } catch (error) {
                                console.error(`Failed to fetch athlete ${email}:`, error);
                            }

                            // Fallback to data from task response
                            return {
                                email,
                                firstName,
                                lastName,
                                imageUrl: undefined
                            };
                        });
                        const athletes = await Promise.all(athletePromises);
                        console.log('Fetched athletes:', athletes); // Debug log
                        setSelectedAthletes(athletes);
                        setAllAthletesSelected(false);
                    } else {
                        console.log('No athlete data found in task'); // Debug log
                    }
                } else {
                    throw new Error(`Failed to fetch task details: ${response.status}`);
                }
            } catch (error: any) {
                console.error('Failed to fetch task details:', error);
                setError(error?.message || "Failed to load task details");
            } finally {
                setLoading(false);
            }
        };

        fetchTaskDetails();
    }, [open, taskId]);

    // Close modal on Escape key
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => (open && e.key === "Escape" ? onClose() : undefined);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    // Reset form when modal closes
    useEffect(() => {
        if (!open) {
            // Only reset when modal is closed
            setTaskData({
                name: "",
                description: "",
                existingFilePath: null,
                dueAt: "",
                allDay: false,
                file: null,
                sendEmail: false
            });
            setError("");
            setTaskOwnerUsername("");
            setSelectedAthletes([]);
            setAllAthletesSelected(false);
            setAthleteInput("");
            setSuggestions([]);
            setShowSuggestions(false);
        }
    }, [open]);

    const formatDueDate = (dueAt: string | undefined) => {
        const isoDate = isoDateFromInstant(dueAt);
        return isoDate ? formatUsDate(isoDate) : "No complete by date";
    };

    // Search athletes as user types (like AddTaskModal)
    useEffect(() => {
        const searchAthletes = async () => {
            if (isAthleteEditing || !athleteInput.trim() || allAthletesSelected) {
                setSuggestions([]);
                return;
            }

            // Check if user typed "all" (case insensitive)
            if (athleteInput.toLowerCase() === "all") {
                setAllAthletesSelected(true);
                setSelectedAthletes([]);
                setAthleteInput("");
                setSuggestions([]);
                setShowSuggestions(false);
                return;
            }

            setSearchingAthletes(true);

            try {
                const response = await fetch(
                    buildApiUrl(`api/athletes/search?query=${encodeURIComponent(athleteInput.trim())}&size=200`),
                    {
                        headers: { Accept: "application/json" },
                        credentials: "include" // Use HTTP-only cookie for auth
                    }
                );

                if (response.status === 401) {
                    window.location.href = '/login';
                    return;
                }

                if (response.ok) {
                    const data = await response.json();
                    const athletes = Array.isArray(data.content) ? data.content : [];

                    // Filter out already selected athletes
                    const filtered = athletes.filter(
                        (a: any) => !selectedAthletes.some(sel => sel.email === a.email)
                    );

                    setSuggestions(filtered.map((a: any) => ({
                        email: a.email || a.id,
                        firstName: a.firstName || "",
                        lastName: a.lastName || "",
                        imageUrl: a.profilePictureUrl || a.imageUrl
                    })));
                    setShowSuggestions(true);
                } else {
                    setSuggestions([]);
                }
            } catch (error) {
                console.error("Failed to search athletes:", error);
                setSuggestions([]);
            } finally {
                setSearchingAthletes(false);
            }
        };

        const timer = setTimeout(searchAthletes, 300);
        return () => clearTimeout(timer);
    }, [athleteInput, selectedAthletes, allAthletesSelected, isAthleteEditing]);

    const handleSave = async () => {
        // Validation
        if (!taskData.name.trim() || taskData.name.trim().length < 2) {
            setError("Task name must be at least 2 characters");
            return;
        }

        if (!taskData.description.trim() || taskData.description.trim().length < 2) {
            setError("Description must be at least 2 characters");
            return;
        }

        if (!isAthleteEditing && !allAthletesSelected && selectedAthletes.length === 0) {
            setError("Please select at least one athlete or 'All'");
            return;
        }

        const selfEmail = (athleteEmail || taskOwnerUsername || "").trim();
        if (isAthleteEditing && !selfEmail) {
            setError("Unable to update this task. Please try again.");
            return;
        }

        const completeBy = parseCompleteByInput(isoDateFromInstant(taskData.dueAt) || taskData.dueAt || "");
        if (!isAthleteEditing && "error" in completeBy) {
            setError(completeBy.error);
            return;
        }

        setSaving(true);
        setError("");

        try {
            const payload = {
                name: taskData.name.trim(),
                description: taskData.description.trim(),
                sendEmail: taskData.sendEmail,
                allAthletes: isAthleteEditing ? false : allAthletesSelected,
                athletes: isAthleteEditing
                    ? [selfEmail]
                    : (allAthletesSelected ? null : selectedAthletes.map(a => a.email)),
                dueAt: "isoDate" in completeBy ? toDueAtPayload(completeBy.isoDate) : taskData.dueAt,
                allDay: true
            };

            const response = await fetch(buildApiUrl(`/api/tasks/${taskId}`), {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: "include", // Use HTTP-only cookie for auth
                body: JSON.stringify(payload)
            });

            if (response.status === 401) {
                window.location.href = '/login';
                return;
            }

            if (response.ok) {
                onTaskUpdated?.();
                onClose();
            } else {
                throw new Error("Unable to update this task. Please try again.");
            }
        } catch (error: any) {
            console.error('Failed to update task:', error);
            setError(error?.message || "Unable to update this task. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const handleSelectAthlete = (athlete: Athlete) => {
        setSelectedAthletes(prev => [...prev, athlete]);
        setAthleteInput("");
        setSuggestions([]);
        setShowSuggestions(false);
        inputRef.current?.focus();
    };

    const handleRemoveAthlete = (email: string) => {
        setSelectedAthletes(prev => prev.filter(a => a.email !== email));
    };

    const handleRemoveAll = () => {
        setAllAthletesSelected(false);
    };

    const handleMarkComplete = async () => {
        setSaving(true);
        setError("");

        try {
            const resolvedAthleteEmail =
                athleteEmail ||
                (selectedAthletes.length > 0 ? selectedAthletes[0].email : '') ||
                taskOwnerUsername;

            if (!resolvedAthleteEmail) {
                setError("Unable to mark task complete: No athlete email found");
                setSaving(false);
                return;
            }

            const response = await fetch(
                buildApiUrl(`api/tasks/${taskId}/athlete/${encodeURIComponent(resolvedAthleteEmail)}/complete`),
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    credentials: "include" // Use HTTP-only cookie for auth
                }
            );

            if (response.status === 401) {
                window.location.href = '/login';
                return;
            }

            if (response.ok) {
                onTaskUpdated?.();
                onClose();
            } else {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }
        } catch (error: any) {
            console.error('Failed to mark task complete:', error);
            setError(error?.message || "Failed to mark task as complete");
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <div className="task-modal-backdrop">
            <div className="task-modal">
                <div className="task-modal-header">
                    <h2>{isEditMode ? "Edit Task" : "View Task"}</h2>
                    <button className="task-modal-close" onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                <div className="task-modal-content">
                    {loading ? (
                        <div className="loading-spinner">Loading task details...</div>
                    ) : (
                        <>
                            {error && (
                                <div className="task-error-message">
                                    {error}
                                </div>
                            )}

                            {isEditMode ? (
                                /* Edit Mode - Use AddTaskModal styling */
                                <>
                                    <div className="task-assignee-section">
                                        <span className="task-label">For</span>

                                        <div className="task-assignee-input-wrapper">
                                            {/* Container that looks like an input field but contains tags + input */}
                                            <div className={`task-assignee-input-container${isAthleteEditing ? " disabled" : ""}`}>
                                                {/* Show "All" tag if selected */}
                                                {allAthletesSelected ? (
                                                    <div className="task-assignee-tag all-tag">
                                                        <div className="assignee-avatar fallback-avatar">
                                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                                                <circle cx="9" cy="7" r="4"/>
                                                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                                            </svg>
                                                        </div>
                                                        <span className="assignee-name">All</span>
                                                        {!isAthleteEditing && (
                                                        <button
                                                            className="assignee-remove"
                                                            onClick={handleRemoveAll}
                                                            aria-label="Remove All"
                                                        >
                                                            ×
                                                        </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <>
                                                        {/* Show selected athlete tags */}
                                                        {selectedAthletes.map(athlete => (
                                                            <div key={athlete.email} className="task-assignee-tag">
                                                                {athlete.imageUrl ? (
                                                                    <img
                                                                        src={athlete.imageUrl}
                                                                        alt={`${athlete.firstName} ${athlete.lastName}`}
                                                                        className="assignee-avatar"
                                                                    />
                                                                ) : (
                                                                    <div className="assignee-avatar fallback-avatar">
                                                                        {athlete.firstName.charAt(0)}{athlete.lastName.charAt(0)}
                                                                    </div>
                                                                )}
                                                                <span className="assignee-name">
                                                                    {athlete.firstName} {athlete.lastName}
                                                                </span>
                                                                {!isAthleteEditing && (
                                                                <button
                                                                    className="assignee-remove"
                                                                    onClick={() => handleRemoveAthlete(athlete.email)}
                                                                    aria-label={`Remove ${athlete.firstName} ${athlete.lastName}`}
                                                                >
                                                                    ×
                                                                </button>
                                                                )}
                                                            </div>
                                                        ))}

                                                        {/* Input field for adding more athletes */}
                                                        {!isAthleteEditing && (
                                                        <input
                                                            ref={inputRef}
                                                            type="text"
                                                            className="athlete-input-inline"
                                                            placeholder={selectedAthletes.length === 0 ? "Type athlete name or 'all'..." : ""}
                                                            value={athleteInput}
                                                            onChange={(e) => setAthleteInput(e.target.value)}
                                                            onFocus={() => setShowSuggestions(true)}
                                                        />
                                                        )}
                                                    </>
                                                )}
                                            </div>

                                            {/* Suggestions dropdown */}
                                            {!isAthleteEditing && !allAthletesSelected && showSuggestions && athleteInput && (
                                                <div className="athlete-suggestions">
                                                    {searchingAthletes ? (
                                                        <div className="suggestion-item loading">Searching...</div>
                                                    ) : athleteSuggestions.length > 0 ? (
                                                        athleteSuggestions.map(athlete => (
                                                            <div
                                                                key={athlete.email}
                                                                className="suggestion-item"
                                                                onClick={() => handleSelectAthlete(athlete)}
                                                            >
                                                                {athlete.imageUrl ? (
                                                                    <img
                                                                        src={athlete.imageUrl}
                                                                        alt={`${athlete.firstName} ${athlete.lastName}`}
                                                                        className="suggestion-avatar"
                                                                    />
                                                                ) : (
                                                                    <div className="suggestion-avatar fallback-avatar">
                                                                        {athlete.firstName.charAt(0)}{athlete.lastName.charAt(0)}
                                                                    </div>
                                                                )}
                                                                <span>{athlete.firstName} {athlete.lastName}</span>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="suggestion-item no-results">No athletes found</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="task-form">
                                        <input
                                            type="text"
                                            placeholder="Task name"
                                            className="task-name-input"
                                            value={taskData.name}
                                            onChange={(e) => setTaskData(prev => ({ ...prev, name: e.target.value }))}
                                            required
                                        />

                                        <textarea
                                            placeholder="Description..."
                                            className="task-description-input"
                                            rows={8}
                                            value={taskData.description}
                                            onChange={(e) => setTaskData(prev => ({ ...prev, description: e.target.value }))}
                                            required
                                        />

                                        <label className="task-complete-by">
                                            <span className="task-label">Complete by:</span>
                                            {isAthleteEditing ? (
                                                <input
                                                    type="text"
                                                    className="task-complete-by-input task-input-readonly"
                                                    value={formatDueDate(taskData.dueAt)}
                                                    readOnly
                                                    aria-label="Complete by"
                                                    data-testid="task-complete-by"
                                                />
                                            ) : (
                                                <div className="complete-by-picker">
                                                    <input
                                                        type="text"
                                                        className="task-complete-by-input"
                                                        value={isoDateFromInstant(taskData.dueAt) ? formatUsDate(isoDateFromInstant(taskData.dueAt)) : ""}
                                                        placeholder="MM/DD/YYYY"
                                                        readOnly
                                                        aria-label="Complete by"
                                                        data-testid="task-complete-by"
                                                        onClick={() => {
                                                            const picker = document.getElementById("edit-task-date-picker") as HTMLInputElement;
                                                            picker?.showPicker?.();
                                                            picker?.focus();
                                                        }}
                                                    />
                                                    <input
                                                        id="edit-task-date-picker"
                                                        type="date"
                                                        className="complete-by-native-picker"
                                                        value={isoDateFromInstant(taskData.dueAt)}
                                                        min={todayIsoDateInUsEastern()}
                                                        onChange={(e) => setTaskData(prev => ({ ...prev, dueAt: e.target.value }))}
                                                        tabIndex={-1}
                                                        aria-hidden="true"
                                                        data-testid="task-complete-by-native"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="complete-by-calendar-btn"
                                                        aria-label="Pick date"
                                                        onClick={() => {
                                                            const picker = document.getElementById("edit-task-date-picker") as HTMLInputElement;
                                                            picker?.showPicker?.();
                                                            picker?.focus();
                                                        }}
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                                                    </button>
                                                </div>
                                            )}
                                        </label>

                                        <div className="task-options">
                                            <label className="checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={taskData.sendEmail}
                                                    onChange={(e) => setTaskData(prev => ({ ...prev, sendEmail: e.target.checked }))}
                                                />
                                                <span className="checkbox-custom"></span>
                                                Send this task as an email
                                            </label>

                                            <button
                                                className="create-task-btn"
                                                onClick={handleSave}
                                                disabled={saving}
                                            >
                                                {saving ? 'Saving...' : 'Save Changes'}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                /* View Mode — same layout as edit, read-only */
                                <>
                                    <div className="task-assignee-section">
                                        <span className="task-label">For</span>

                                        <div className="task-assignee-input-wrapper">
                                            <div
                                                className="task-assignee-input-container disabled"
                                                data-testid="view-task-assignees"
                                            >
                                                {allAthletesSelected ? (
                                                    <div className="task-assignee-tag all-tag">
                                                        <div className="assignee-avatar fallback-avatar">
                                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                                                <circle cx="9" cy="7" r="4"/>
                                                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                                                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                                            </svg>
                                                        </div>
                                                        <span className="assignee-name">All</span>
                                                    </div>
                                                ) : selectedAthletes.length > 0 ? (
                                                    selectedAthletes.map(athlete => (
                                                        <div key={athlete.email} className="task-assignee-tag">
                                                            {athlete.imageUrl ? (
                                                                <img
                                                                    src={athlete.imageUrl}
                                                                    alt={`${athlete.firstName} ${athlete.lastName}`}
                                                                    className="assignee-avatar"
                                                                />
                                                            ) : (
                                                                <div className="assignee-avatar fallback-avatar">
                                                                    {athlete.firstName.charAt(0)}{athlete.lastName.charAt(0)}
                                                                </div>
                                                            )}
                                                            <span className="assignee-name">
                                                                {athlete.firstName} {athlete.lastName}
                                                            </span>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <span className="task-assignee-empty">No athletes</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="task-form task-form--readonly">
                                        <input
                                            type="text"
                                            placeholder="Task name"
                                            className="task-name-input task-input-readonly"
                                            value={taskData.name}
                                            readOnly
                                            data-testid="view-task-name"
                                        />

                                        <textarea
                                            placeholder="Description..."
                                            className="task-description-input task-input-readonly"
                                            rows={8}
                                            value={taskData.description}
                                            readOnly
                                            data-testid="view-task-description"
                                        />

                                        <label className="task-complete-by">
                                            <span className="task-label">Complete by:</span>
                                            <input
                                                type="text"
                                                className="task-complete-by-input task-input-readonly"
                                                value={formatDueDate(taskData.dueAt)}
                                                readOnly
                                                aria-label="Complete by"
                                                data-testid="view-task-due-date"
                                            />
                                        </label>

                                        {taskData.existingFilePath && (
                                            <div className="file-drop-zone file-drop-zone--readonly">
                                                <div className="file-selected">
                                                    <span className="file-name">
                                                        📄 {taskData.existingFilePath.split('/').pop()}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        <div className="task-options">
                                            {!taskData.completed && (
                                                <button
                                                    className="mark-complete-btn"
                                                    onClick={handleMarkComplete}
                                                    disabled={saving}
                                                    data-testid="view-task-mark-complete"
                                                >
                                                    {saving ? 'Marking complete...' : 'Mark as complete'}
                                                </button>
                                            )}
                                            <button
                                                className="close-task-btn"
                                                onClick={onClose}
                                                data-testid="view-task-close"
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
